/**
 * EmailToolsCard — Widget SuperAdmin pentru gestionarea email-urilor folosite.
 *
 *  • Verifică status email (e folosit? cine îl folosește? e orfan?)
 *  • Eliberează forțat un email (șterge complet contul + datele firmei)
 *
 * Util pentru cazurile când un user vrea să reînregistreze cu un email pe care
 * l-a folosit anterior pe o firmă de test ștearsă incomplet.
 */
import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, AlertTriangle, ShieldOff, Mail, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailToolsCard() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const handleCheck = async (e) => {
    e?.preventDefault?.();
    const norm = email.trim().toLowerCase();
    if (!norm) {
      toast.error("Introdu un email");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.get(`${API}/superadmin/email-status?email=${encodeURIComponent(norm)}`);
      setResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la verificare");
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async () => {
    if (!result?.in_use) return;
    const accounts = result.accounts || [];
    const masterCount = accounts.filter((a) => a.role === "master").length;
    const confirmMsg = masterCount > 0
      ? `Vei ȘTERGE COMPLET ${accounts.length} cont(uri) și datele a ${masterCount} firmă/firme (sites, devize, facturi, etc). Operațiunea NU se poate anula. Continuă?`
      : `Vei ȘTERGE ${accounts.length} cont(uri) pentru ${result.email}. Continuă?`;
    if (!window.confirm(confirmMsg)) return;
    setReleasing(true);
    try {
      const r = await axios.delete(`${API}/superadmin/email-release?email=${encodeURIComponent(result.email)}`);
      toast.success(`Email eliberat! ${r.data.deleted_users} cont(uri) șters(e).`);
      setResult(null);
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la eliberare");
    } finally {
      setReleasing(false);
    }
  };

  return (
    <Card className="bg-white/45 backdrop-blur-xl border-white/60 mb-4" data-testid="email-tools-card">
      <CardHeader>
        <CardTitle className="text-slate-800 flex items-center gap-2 text-base">
          <Mail className="w-5 h-5 text-teal-600" /> Gestionare email-uri
        </CardTitle>
        <CardDescription className="text-slate-500">
          Verifică dacă un email e folosit pe platformă și, dacă e necesar, eliberează-l forțat (pentru conturi de test sau abandonate).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleCheck} className="flex flex-col sm:flex-row gap-2 mb-3">
          <Input
            type="email"
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || releasing}
            className="flex-1"
            data-testid="email-tools-input"
          />
          <Button
            type="submit"
            disabled={loading || releasing}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="email-tools-check"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Verifică
          </Button>
        </form>

        {result && (
          <div className="border-t pt-3 mt-3 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              {result.in_use ? (
                <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" /> Email folosit ({result.count} cont{result.count !== 1 ? "uri" : ""})</Badge>
              ) : (
                <Badge className="bg-green-600 gap-1"><CheckCircle2 className="w-3 h-3" /> Email LIBER — disponibil pentru înregistrare</Badge>
              )}
            </div>

            {result.in_use && (
              <>
                <div className="space-y-1.5">
                  {result.accounts.map((a) => (
                    <div key={a.id} className="bg-slate-50 border rounded p-2 text-xs">
                      <div className="font-semibold text-slate-800 flex items-center gap-2">
                        {a.name || "—"}
                        <Badge variant="outline" className="text-[10px]">{a.role}</Badge>
                        {a.is_orphan && <Badge className="bg-amber-500 text-[10px]">ORFAN</Badge>}
                        {a.email_verified === false && <Badge variant="outline" className="text-[10px] text-amber-600">neverificat</Badge>}
                      </div>
                      <div className="text-slate-500 mt-0.5">
                        {a.company_name ? `Firma: ${a.company_name}` : "Fără firmă atașată"}
                        {a.created_at && <span className="ml-2">· creat: {String(a.created_at).slice(0, 10)}</span>}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-red-50 border border-red-200 rounded p-2.5 mt-2">
                  <div className="flex items-start gap-2 text-xs text-red-800">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Atenție:</strong> Eliberarea șterge IREVERSIBIL toate conturile + datele firmelor asociate (sites, devize, facturi, etc.).
                      Folosește doar pentru conturi de test / abandonate / cu permisiunea explicită a deținătorului.
                    </div>
                  </div>
                  <Button
                    onClick={handleRelease}
                    disabled={releasing}
                    variant="destructive"
                    size="sm"
                    className="mt-2 w-full"
                    data-testid="email-tools-release"
                  >
                    {releasing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldOff className="w-4 h-4 mr-2" />}
                    Eliberează email-ul forțat
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
